import axios from "axios";
import { authStore } from "@workspace/shared/core/store/auth/auth.store";
import { Thumbmark } from "@thumbmarkjs/thumbmarkjs";
import { getEnv } from "@workspace/shared/core/env";
import authRoutes from "@workspace/shared/core/services/auth/auth.routes";
import { getAppKey } from "@workspace/shared/core/store/selected-app/selected-app.store";
import { app_version } from "@workspace/shared/versioning";

/**
 * Dispatched on `window` when a request 401s and the refresh token can no
 * longer mint a new access token (expired/revoked). Listeners (AuthContext)
 * offer an IN-PLACE re-login — never a logout, which would wipe the device's
 * unsynced plays.
 */
export const SESSION_EXPIRED_EVENT = "mofo:session-expired";

/**
 * Why the session died, as `detail` of a SESSION_EXPIRED_EVENT: "expired"
 * (the default — the refresh token is dead, or the API keeps refusing the
 * session) or "released" (the session socket said this device was signed out
 * from another one).
 */
export type SessionExpiredReason = "expired" | "released";

/**
 * Dispatched on `window` after the coach signs in again in place. Signing in
 * clears the device's revoked session, so the app registers it again (the
 * device limit, Active Sessions and admin revoke need it).
 */
export const SIGNED_IN_AGAIN_EVENT = "mofo:signed-in-again";

const api = axios.create({
  baseURL: "",
  // A default ceiling so a blackholed / half-dead connection (the API
  // unreachable while the device still reports "online" — stadium wifi, a
  // captive portal) REJECTS instead of hanging forever. A per-request
  // `timeout` still overrides it (the QB report sets 180 s), and a timed-out
  // upload stays queued and is retried by the pending sync — no data is lost.
  // Ported from the in-game app.
  timeout: 30_000,
  headers: {},
});

const updateApiConfig = () => {
  try {
    const env = getEnv();
    api.defaults.baseURL = env.API_URL;
    api.defaults.headers["x-app-key"] = env.API_KEY;
  } catch {
    // Env not initialized yet — the request interceptor will call this again
    // before the first request goes out, so this is expected on initial load.
  }
};
updateApiConfig();

// The device id, fingerprinted once. On some devices (notably Safari with
// tracking protection) fingerprinting never settles, so every wait is capped:
// a hung Thumbmark must not keep requests from ever leaving the browser.
// Ported from the in-game app.
const FINGERPRINT_TIMEOUT_MS = 2_500;
let cachedDeviceId: string | null = null;
let fingerprintPromise: Promise<string> | null = null;

// When fingerprinting fails or times out, fall back to a RANDOM id kept in
// localStorage — never a shared constant like "unknown", which made every
// such device the SAME device to the session system (one slot for all of
// them, and a revoke of one signed out the rest). Same key as the in-game
// app, so a device keeps one id across both products.
const FALLBACK_DEVICE_ID_KEY = "mf.device-id";
const getFallbackDeviceId = (): string => {
  try {
    let id = localStorage.getItem(FALLBACK_DEVICE_ID_KEY);
    if (!id) {
      id = `fallback-${
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2)}`
      }`;
      localStorage.setItem(FALLBACK_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "unknown";
  }
};

const computeFingerprint = (): Promise<string> => {
  if (!fingerprintPromise) {
    fingerprintPromise = new Thumbmark()
      .get()
      .then((fingerprint): string => {
        // First writer wins: if the cap already pinned this page to the
        // fallback id, keep it — switching ids mid-session would look like
        // another device to the server.
        const resolved = cachedDeviceId ?? (fingerprint?.thumbmark || getFallbackDeviceId());
        cachedDeviceId = resolved;
        return resolved;
      })
      .catch((): string => {
        const resolved = cachedDeviceId ?? getFallbackDeviceId();
        cachedDeviceId = resolved;
        return resolved;
      });
  }
  return fingerprintPromise;
};

/**
 * The id this device sends as `x-device-id`. Exported so the session socket
 * hands the server the SAME id (it spares the device that asked for "close
 * the other sessions" by it).
 */
export const getDeviceId = (): Promise<string> => {
  if (cachedDeviceId) return Promise.resolve(cachedDeviceId);
  // One id per device across loads: the first one it settles on is kept and
  // reused. Racing the fingerprint against the cap on every load gave a device
  // two ids — the fallback on a slow load, the fingerprint on a fast one — so
  // the server saw two devices: a second session slot, and a revoke of one
  // "signing out" the other (audit 2026-10-19).
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(FALLBACK_DEVICE_ID_KEY);
  } catch {
    stored = null;
  }
  if (stored) {
    cachedDeviceId = stored;
    return Promise.resolve(stored);
  }
  return Promise.race([
    computeFingerprint(),
    new Promise<string>((resolve) =>
      setTimeout(() => {
        const resolved = cachedDeviceId ?? getFallbackDeviceId();
        cachedDeviceId = resolved;
        resolve(resolved);
      }, FINGERPRINT_TIMEOUT_MS)
    ),
  ]).then((id) => {
    try {
      if (id && id !== "unknown") localStorage.setItem(FALLBACK_DEVICE_ID_KEY, id);
    } catch {
      // Storage blocked: this page keeps its id; the next load races again.
    }
    return id;
  });
};

// "Tablet", "Mobile" or "Laptop", for the session record (admin Active
// Sessions, telemetry). iPadOS Safari reports a Mac User-Agent, so touch
// points tell an iPad from a laptop. Computed once per page.
let cachedDeviceType: string | null = null;
const getDeviceType = (): string => {
  if (cachedDeviceType) return cachedDeviceType;
  let type = "Laptop";
  try {
    const ua = navigator.userAgent || "";
    const touch = navigator.maxTouchPoints ?? 0;
    if (/iPhone|iPod/.test(ua)) type = "Mobile";
    else if (/Android/.test(ua)) type = /Mobile/.test(ua) ? "Mobile" : "Tablet";
    else if (/iPad/.test(ua)) type = "Tablet";
    else if (/Macintosh/.test(ua) && touch > 1) type = "Tablet";
  } catch {
    type = "Laptop";
  }
  cachedDeviceType = type;
  return type;
};

api.interceptors.request.use(
  async (config) => {
    updateApiConfig();
    if (!api.defaults.baseURL || api.defaults.baseURL === "" || !config.baseURL) {
      const env = getEnv();
      config.baseURL = env.API_URL;
      config.headers["x-app-key"] = env.API_KEY;
    }
    // The app this request is for, read BEFORE the await below: a key set
    // while the device id resolves belongs to a later request.
    const appKeyAtCall = getAppKey();
    const deviceId = await getDeviceId();
    config.headers["x-device-id"] = deviceId;
    // The device's form factor and build, recorded on its session so admin
    // and support can see what a coach runs.
    config.headers["x-device-type"] = getDeviceType();
    config.headers["x-app-version"] = app_version;
    // Honor an explicitly-provided app header. The cross-app pending sweep
    // stamps `x-app-selected` per request so a concurrent mutation of the
    // shared global app key (e.g. the dashboard setting it to "Dashboard" on
    // reconnect) can't misroute an app's uploads to the wrong bucket.
    if (config.headers["x-app-selected"] == null) {
      config.headers["x-app-selected"] = appKeyAtCall || undefined;
    }

    const jwt = authStore.getState().accessToken;
    if (jwt && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${jwt}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);
// Refresh-token queue, copied from the in-game app: only ONE refresh runs at
// a time. If multiple requests get 401 concurrently, they all wait on the same
// refresh instead of firing parallel refresh calls (which breaks with rotated
// tokens).
let refreshPromise: Promise<string | null> | null = null;

async function attemptTokenRefresh(): Promise<string | null> {
  // NOTE: refreshPromise is cleared by the .finally() attached at the
  // assignment site, NOT inside this function. Clearing it in here is a
  // trap: a synchronous completion (no stored refresh token → immediate
  // return) would run before the caller's `refreshPromise = ...` assignment
  // lands, leaving a permanently-settled promise that disables refresh for
  // the rest of the page's life. Errors propagate to the interceptor's catch.
  const refreshTokenValue = authStore.getState().refreshToken;
  if (!refreshTokenValue) return null;

  const deviceId = await getDeviceId();
  const env = getEnv();
  const { data } = await axios.post(
    `${env.API_URL}${authRoutes.refreshTokenRoute}`,
    { refreshToken: refreshTokenValue },
    {
      timeout: 30_000,
      headers: {
        "x-device-id": deviceId,
        "x-app-key": env.API_KEY,
      },
    }
  );
  // The coach signed in again while this was on the wire: that session is
  // the one to keep. Writing this answer would put the old one back.
  if (authStore.getState().refreshToken !== refreshTokenValue) {
    return authStore.getState().accessToken;
  }
  if (data?.accessToken) {
    // Save ALL returned tokens — the server may rotate the refresh token.
    // Saving only accessToken (the old behavior) invalidated the stored
    // refreshToken on the next refresh cycle → false 401 → dead session.
    authStore.setState({
      accessToken: data.accessToken,
      ...(data.refreshToken && { refreshToken: data.refreshToken }),
      ...(data.idToken && { idToken: data.idToken }),
    });
    return data.accessToken;
  }

  return null;
}

/**
 * The request went out on an older session than the one the device holds now
 * (the coach signed in again, or another request's refresh landed): retry it
 * once on the current token instead of refreshing or asking.
 */
const retryOnCurrentSession = (request: any): Promise<any> | null => {
  const current = authStore.getState().accessToken;
  const sent = String(request?.headers?.Authorization ?? "");
  if (!current || !sent || sent === `Bearer ${current}`) return null;
  if (request._retriedOnCurrentSession) return null;
  request._retriedOnCurrentSession = true;
  request.headers.Authorization = `Bearer ${current}`;
  return api(request);
};

/** Ask the coach to sign in again in place (AuthContext shows the prompt). */
const announceSessionExpired = (url: string) => {
  // A failing LOGOUT means the coach is signing out on purpose: no prompt.
  if (
    authStore.getState().refreshToken &&
    typeof window !== "undefined" &&
    !url.includes(authRoutes.logoutRoute)
  ) {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config ?? {};
    const url = String(originalRequest?.url ?? "");
    // Auth endpoints 401 on bad credentials — refreshing/retrying them would
    // loop or mask the real error.
    const isAuthRoute =
      url.includes("auth/login") ||
      url.includes("auth/refresh-token") ||
      url.includes("auth/revoke-other-sessions");

    // Only attempt refresh on 401, only once per request, and only while a
    // refresh token exists. (Deliberately NOT gated on the token's local exp
    // — a skewed device clock can hold a token the server already rejects.)
    if (error.response?.status === 401 && !isAuthRoute) {
      const onCurrent = retryOnCurrentSession(originalRequest);
      if (onCurrent) return onCurrent;
    }
    if (
      error.response?.status === 401 &&
      originalRequest._retry &&
      !isAuthRoute
    ) {
      // Refreshed and STILL refused: a device signed out from another one
      // (or by an admin) gets fresh tokens from Cognito that the API keeps
      // rejecting, so refreshing can never heal it. It used to fail silently
      // here — grading kept saving on the device and stopped uploading, with
      // no prompt (ported from the in-game app).
      announceSessionExpired(url);
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute &&
      authStore.getState().refreshToken
    ) {
      originalRequest._retry = true;

      try {
        // If a refresh is already in-flight, wait for it instead of firing a
        // second refresh (which would race / invalidate rotated tokens). The
        // .finally() here (not inside attemptTokenRefresh) is what clears the
        // slot — it always runs AFTER this assignment has landed.
        if (!refreshPromise) {
          refreshPromise = attemptTokenRefresh().finally(() => {
            refreshPromise = null;
          });
        }

        const newToken = await refreshPromise;
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError: any) {
        console.warn("[api] Token refresh failed:", refreshError);
        // Refresh failed for a reason that says nothing about the session —
        // no answer at all, or a server that is down or throttling (5xx,
        // 429, e.g. during a deploy): propagate THAT error, not the original
        // 401, so callers treat it as transient instead of "session expired".
        const status = refreshError?.response?.status;
        const isTransient =
          !refreshError?.response || status >= 500 || status === 429;
        if (isTransient) {
          return Promise.reject(refreshError);
        }
        // Server rejected the refresh (expired/revoked refresh token) → fall
        // through: announce the dead session, propagate the original 401.
      }

      // Signed in again meanwhile: the new session may take it.
      const afterSignIn = retryOnCurrentSession(originalRequest);
      if (afterSignIn) return afterSignIn;
      // The refresh token itself is dead: the session can't self-heal. Tell
      // the UI so it can offer an in-place re-login (tokens swap, local data
      // kept) — never the destructive logout. Exception: a failing LOGOUT
      // request means the coach is deliberately signing out — popping a
      // "sign in again" modal mid-sign-out would be absurd; the sign-out
      // guard already protects any unsynced data on that path.
      announceSessionExpired(url);
    }

    return Promise.reject(error);
  }
);

export { api };
